import { Injectable, signal, inject } from '@angular/core';
import { EnrollhereApiService } from '../enrollhere-api/enrollhere-api';

export interface Carrier {
  id: string;
  name: string;
  image?: string;
  color?: string;
  colorOklch?: string;
  textColorOklch?: string;
}

export interface CarrierListResponse {
  carriers: Carrier[];
  total: number;
}

/**
 * Carrier Configuration Service
 * Loads and caches carrier data from enrollhere API
 * Reusable across all products
 */
@Injectable({
  providedIn: 'root',
})
export class CarrierConfigService {
  private readonly enrollhereApi = inject(EnrollhereApiService);

  carriers = signal<Carrier[]>([]);
  carrierMap = signal<{ [key: string]: Carrier }>({});
  loading = signal(false);
  loaded = signal(false);

  async loadCarriers(): Promise<Carrier[]> {
    if (this.loaded()) {
      return this.carriers();
    }

    if (this.loading()) {
      while (!this.loaded()) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      return this.loadCarriers();
    }

    this.loading.set(true);

    try {
      const res =
        await this.enrollhereApi.get<CarrierListResponse>('config/carriers');

      if (res.carriers) {
        this.carriers.set(res.carriers);
        this.carrierMap.set(
          res.carriers.reduce(
            (acc, carrier) => {
              acc[carrier.id] = carrier;
              return acc;
            },
            {} as { [key: string]: Carrier }
          )
        );
        this.loaded.set(true);
        this.loading.set(false);
        return res.carriers;
      }
    } catch (error) {
      console.error('Error loading carriers', error);
      this.loading.set(false);
    }
    return [];
  }
}
